import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap, tap } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import * as DecoupledEditor from '@ckeditor/ckeditor5-build-decoupled-document';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EmailCampaignService } from '../services/email-campaign.service';
import { EmailTemplateService } from '../services/email-template.service';
import { UserMenuService } from '../services/user-menu.service';
import { UserService } from '../services/user.service';
import { EmailTemplate } from '../models/email-template.model';
import { environment } from '../../../environments/environment';
import {
  CampaignBccPolicy,
  CampaignCreateResponse,
  CampaignPreviewResponse,
  CampaignStatus,
} from '../models/email-campaign.model';

// All fields optional - the wizard resolves them itself (template step, recipients step)
// when the caller doesn't already have a template/recipients in hand.
export interface CampaignSendDialogData {
  templateId?: number;
  fromEmail?: string;
  subject?: string;
  body?: string;
  recipientGroupIds?: number[];
  explicitRecipientEmails?: string[];
  cc?: string[];
  bcc?: string[];
}

interface GroupOption {
  id: number;
  name: string;
  created_at?: string;
}

type WizardStep = 'template' | 'recipients' | 'preview' | 'confirm' | 'sending' | 'status';

@Component({
  selector: 'app-campaign-send-dialog',
  templateUrl: './campaign-send-dialog.component.html',
  styleUrls: ['./campaign-send-dialog.component.scss']
})
export class CampaignSendDialogComponent implements OnInit, OnDestroy {

  step: WizardStep;
  loading = false;
  error: string;

  // Same rich-text editor as the template edit dialog, reused here for a consistent authoring experience.
  public Editor = DecoupledEditor;
  public editorConfig = {
    placeholder: 'Write a Message',
    fontSize: {
      options: [9, 11, 13, 'default', 17, 19, 21, 28, 30, 41]
    },
    toolbar: ['heading', 'fontColor', 'fontFamily', 'fontSize', 'Highlight', '|', 'bold', 'italic', 'Underline', 'strikeThrough', '|', 'alignment', 'bulletedList', 'numberedList', 'blockQuote', 'link'],
  };
  NickName = '{{Nickname}}';
  FirstName = '{{Firstname}}';

  public onReady(editor) {
    editor.ui.getEditableElement().parentElement.insertBefore(
      editor.ui.view.toolbar.element,
      editor.ui.getEditableElement()
    );
  }

  // Step: template
  templateTab: 'clients' | 'artists' = 'clients';
  loadingTemplates = false;
  loadingTemplateDetail = false;
  templates: EmailTemplate[] = [];
  selectedTemplate: EmailTemplate;
  templateSearchString = '';
  templateSearchFocused = false;

  // Step: recipients - same fields as the template dialog's edit form (from/to group/individual/cc/bcc/subject/body)
  fromEmail: string;
  fromEmailOptions: { email: string; id: number }[] = [];
  groupOptions: GroupOption[] = [];
  loadingGroups = false;
  groupSearchCtrl = new FormControl();
  searchingGroups = false;
  filteredGroupOptions: BehaviorSubject<GroupOption[]> = new BehaviorSubject<GroupOption[]>([]);
  recipientGroupIds: number[] = [];
  individualRecipient = '';
  ccInput = '';
  bccInput = '';
  subject = '';
  body = '';
  recipientsError: string;

  // Playlists - the primary purpose of the template/campaign system: attach one or more
  // playlists, which get appended to the message body as a linked list before sending.
  playlistIndex = 1;
  loadingPlaylists = false;
  playlistArray: any[] = [];
  playListArrayNames: any[] = [];
  playlistSearchString = '';
  playlistSearchFocused = false;
  searchResultsCount = 0;
  recipientAccountPlaylistIds: number[] = [];
  private _preselectPlaylists: any[] = [];

  // Steps: preview / confirm / send / status
  bccPolicy: CampaignBccPolicy = 'SEPARATE_SUMMARY';
  previewResponse: CampaignPreviewResponse;
  createResponse: CampaignCreateResponse;
  campaignStatus: CampaignStatus;
  pollError: string;
  pollFailed = false;

  // Reused only when retrying the same request after a network failure - a fresh
  // key is generated for every new user-initiated create/send click.
  private _createIdempotencyKey: string;
  private _sendIdempotencyKey: string;
  private _statusSubscription: Subscription;

  constructor(
    private dialogRef: MatDialogRef<CampaignSendDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CampaignSendDialogData,
    private _campaignService: EmailCampaignService,
    private _emailTemplateService: EmailTemplateService,
    private _api: UserMenuService,
    private _userService: UserService,
    private _clipboard: Clipboard,
    private _snackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    this.groupSearchCtrl.valueChanges
      .pipe(
        startWith(''),
        debounceTime(150),
        distinctUntilChanged(),
        tap(() => { this.searchingGroups = true; }),
        switchMap((v) => {
          if (!v) {
            return of(this.groupOptions);
          }
          return of(this.groupOptions.filter((g) => g.name.trim().toLowerCase().includes(v.toLowerCase())));
        }),
        tap(() => { this.searchingGroups = false; })
      )
      .subscribe((g) => this.filteredGroupOptions.next(g));

    if (this.data && this.data.templateId) {
      // Called with a template already chosen - skip straight to recipients.
      this.subject = this.data.subject || '';
      this.body = this.data.body || '';
      this.fromEmail = this.data.fromEmail;
      this.recipientGroupIds = this.data.recipientGroupIds || [];
      this.individualRecipient = (this.data.explicitRecipientEmails || [])[0] || '';
      this.ccInput = (this.data.cc || [])[0] || '';
      this.bccInput = (this.data.bcc || [])[0] || '';
      this.selectedTemplate = new EmailTemplate({ id: this.data.templateId });
      this.step = 'recipients';
      this.loadFromEmails();
      this.loadGroups();
      this.loadPlaylists();
    } else {
      this.step = 'template';
      this.switchTemplateTab(this.templateTab);
    }
  }

  ngOnDestroy(): void {
    this._statusSubscription?.unsubscribe();
  }

  // ---- Step: template ----

  switchTemplateTab(tab: 'clients' | 'artists') {
    this.templateTab = tab;
    this.templateSearchString = '';
    this.loadTemplates();
  }

  submitTemplateSearch() {
    this.templateSearchFocused = false;
    this.loadTemplates();
  }

  private loadTemplates() {
    this.loadingTemplates = true;
    this._emailTemplateService.getAllTemplates({
      type: this.templateTab === 'clients' ? 'CLIENT' : 'ARTIST',
      limit: 100,
      searchString: this.templateSearchString || undefined,
    }).subscribe((res) => {
      this.loadingTemplates = false;
      this.templates = res.results;
    }, (err) => {
      this.loadingTemplates = false;
      this.error = this._errorMessage(err);
    });
  }

  // Loads the full template - same content/metadata (subject, body, groups, cc/bcc) the
  // template tab's edit dialog loads - rather than trusting the list summary alone.
  selectTemplate(template: EmailTemplate) {
    this.loadingTemplateDetail = true;
    this.error = null;
    this._emailTemplateService.getTemplate(template.id).subscribe((full) => {
      this.loadingTemplateDetail = false;
      this.applyTemplateSelection(full);
    }, (err) => {
      this.loadingTemplateDetail = false;
      this.error = this._errorMessage(err);
    });
  }

  private applyTemplateSelection(template: EmailTemplate) {
    this.selectedTemplate = template;
    this.subject = template.subject || '';
    this.body = template.body || '';
    this.recipientGroupIds = (template.groups || []).map((g: any) => (g && typeof g === 'object') ? g.id : g);
    this.individualRecipient = template.receiver || '';
    this.ccInput = Array.isArray(template.cc_email) ? (template.cc_email[0] || '') : (template.cc_email || '');
    this.bccInput = Array.isArray(template.bcc_email) ? (template.bcc_email[0] || '') : (template.bcc_email || '');
    this.recipientsError = null;
    this._preselectPlaylists = template.playlists || [];
    this.playlistArray = [];
    this.playListArrayNames = [];
    this.recipientAccountPlaylistIds = [];
    this.step = 'recipients';
    this.loadFromEmails();
    this.loadGroups();
    this.loadPlaylists();
  }

  // Backward navigation for a poor template/recipient selection - clears the step ahead
  // of the one being returned to so stale results aren't shown again.
  backToTemplateStep() {
    this.step = 'template';
    this.selectedTemplate = null;
  }

  backToRecipientsStep() {
    this.previewResponse = null;
    this.step = 'recipients';
  }

  backToPreviewStep() {
    this.step = 'preview';
  }

  // ---- Step: recipients ----

  private loadFromEmails() {
    if (this.fromEmailOptions.length > 0) {
      return;
    }
    let requestUrl = `${environment.apiURL}/api/v1/admin/emailtemp/get_from_emails/`;
    this._api.doGET(requestUrl).subscribe((x: any) => {
      let data = x['from_emails'] || [];
      for (let key in data) {
        this.fromEmailOptions.push({ email: data[key].sendAsEmail, id: Number(key) });
      }
      if (!this.fromEmail && this.fromEmailOptions.length > 0) {
        this.fromEmail = this.fromEmailOptions[0].email;
      }
    }, () => { });
  }

  private loadGroups() {
    let type = this.templateTab === 'artists' ? 'ARTIST' : 'CLIENT';
    let requestUrl = `${environment.apiURL}/api/v1/admin/emailgroup/email-group-dropdown/?type=${type}`;
    this.loadingGroups = true;
    this._api.doGET(requestUrl).subscribe((x: any) => {
      this.loadingGroups = false;
      this.groupOptions = x || [];
      this.filteredGroupOptions.next(this.groupOptions);
    }, () => { this.loadingGroups = false; });
  }

  // ---- Playlists ----

  submitPlaylistSearch() {
    this.playlistSearchFocused = false;
    this.loadPlaylists();
  }

  loadMorePlaylists() {
    this.playlistIndex++;
    this.loadPlaylists(false);
  }

  private loadPlaylists(reset: boolean = true) {
    if (reset) {
      this.playlistIndex = 1;
    }
    this.loadingPlaylists = true;
    let requestUrl = `${environment.apiURL}/api/v1/admin/playlists/dropdown/?search=${this.playlistSearchString}&page=${this.playlistIndex}`;
    this._api.doGET(requestUrl).subscribe((x: any) => {
      this.loadingPlaylists = false;
      this.searchResultsCount = x['count'];
      if (reset) {
        this.playlistArray = [];
      }
      for (let playlist of x['results']) {
        this.playlistArray.push(playlist);
      }
      this._applyPreselectedPlaylists();
    }, () => { this.loadingPlaylists = false; });
  }

  // Marks playlists already attached to the selected template as checked, same as the
  // template edit dialog does for an existing template's playlists.
  private _applyPreselectedPlaylists() {
    this._preselectPlaylists.forEach((selected: any) => {
      let selectedId = (selected && typeof selected === 'object') ? selected.id : selected;
      for (let playlist of this.playlistArray) {
        if (playlist.id === selectedId && !playlist.checked) {
          playlist.checked = true;
          this.playListArrayNames.push(playlist);
        }
      }
    });
  }

  // Mirrors the template dialog's mySelectOpt: toggling a playlist inserts/removes a
  // "* MIBE <name>" bullet line in the message body itself, not just an internal list.
  togglePlaylist(playlist: any, evt: any) {
    let bulletLine = '* MIBE ' + playlist.name;
    if (evt.checked) {
      playlist.checked = true;
      if (!this.playListArrayNames.some((p) => p.id === playlist.id)) {
        this.playListArrayNames.push(playlist);
      }
      let cleaned = (this.body || '').replace(bulletLine, '');
      cleaned = cleaned.replace(/^(?=\n)$|^\s*|\s*$|\n\n+/gm, '');
      this.body = cleaned ? cleaned + '\n' + bulletLine : bulletLine;
    } else {
      playlist.checked = false;
      this.playListArrayNames = this.playListArrayNames.filter((p) => p.id !== playlist.id);
      let cleaned = (this.body || '').replace(bulletLine, '');
      this.body = cleaned.replace(/^(?=\n)$|^\s*|\s*$|\n\n+/gm, '');
    }
  }

  copyPlaylistLink(playlist: any) {
    let copiedURL = 'https://democlient.mibemusic.xyz/playlists?playlist=' + playlist.id + '&ordering_by=id';
    this._clipboard.copy(copiedURL);
    this._snackBar.open('Link Copied', '', { duration: 3000 });
  }

  // Adds the playlist directly to each recipient's account, separate from the message-body link.
  toggleAddToRecipientAccount(playlist: any, evt: any) {
    if (evt.checked) {
      if (!this.recipientAccountPlaylistIds.includes(playlist.id)) {
        this.recipientAccountPlaylistIds.push(playlist.id);
      }
    } else {
      this.recipientAccountPlaylistIds = this.recipientAccountPlaylistIds.filter((id) => id !== playlist.id);
    }
  }

  // Wraps the plain message with a linked playlist list, matching the legacy send flow's
  // htmlBody construction, so the campaign body actually contains clickable playlist links.
  private buildFinalBody(): string {
    if (this.playListArrayNames.length === 0) {
      return '<html><body>' + this.body + '</body></html>';
    }
    let user = this._userService.currentUser;
    let sharedBy = user ? (user.first_name + ' ' + user.last_name) : '';
    let items = this.playListArrayNames.map((p) =>
      '<li><a target=\'_blank\' href="https://mibemusic.com/playlist/' + p.id + '" class="action">MIBE ' + p.name + '</a></li>'
    ).join('');
    return '<html><body><h3>' + this.body + '</h3><br><p>Playlist shared by <strong>' + sharedBy + '</strong></p><ul>' + items + '</ul></body></html>';
  }


  proceedToPreview() {
    this.recipientsError = null;
    if (!this.subject || !this.subject.trim()) {
      this.recipientsError = 'Subject is required';
      return;
    }
    if (!this.body || !this.body.trim()) {
      this.recipientsError = 'Message body is required';
      return;
    }
    if (!this.fromEmail) {
      this.recipientsError = 'From email is required';
      return;
    }
    if (this.recipientGroupIds.length === 0 && !this.individualRecipient) {
      this.recipientsError = 'Select at least one recipient group or an individual recipient';
      return;
    }
    this.runPreview();
  }

  private get _explicitRecipientEmails(): string[] {
    return this.individualRecipient ? [this.individualRecipient] : [];
  }

  private get _cc(): string[] {
    return this.ccInput ? [this.ccInput] : [];
  }

  private get _bcc(): string[] {
    return this.bccInput ? [this.bccInput] : [];
  }

  // ---- Steps: preview / confirm / send / status ----

  runPreview() {
    this.step = 'preview';
    this.loading = true;
    this.error = null;
    this._campaignService.preview({
      template_id: this.selectedTemplate?.id,
      recipient_group_ids: this.recipientGroupIds,
      explicit_recipient_emails: this._explicitRecipientEmails,
      bcc: this._bcc,
      bcc_policy: this.bccPolicy,
    }).subscribe((res) => {
      this.loading = false;
      this.previewResponse = res;
    }, (err) => {
      this.loading = false;
      this.error = this._errorMessage(err);
    });
  }

  createCampaign() {
    this.loading = true;
    this.error = null;
    this._createIdempotencyKey = this._createIdempotencyKey || (crypto as any).randomUUID();
    this._campaignService.createCampaign({
      template_id: this.selectedTemplate?.id,
      from_email: this.fromEmail,
      subject: this.subject,
      body: this.buildFinalBody(),
      recipient_group_ids: this.recipientGroupIds,
      explicit_recipient_emails: this._explicitRecipientEmails,
      cc: this._cc,
      bcc: this._bcc,
      bcc_policy: this.bccPolicy,
      idempotency_key: this._createIdempotencyKey,
      auto_send: false,
      included_playlist_ids: this.playListArrayNames.map((playlist) => playlist.id),
      recipient_playlist_ids: this.recipientAccountPlaylistIds,
    }).subscribe((res) => {
      this.loading = false;
      this.createResponse = res;
      // A fresh key is only needed if the user retries after a genuine failure.
      this._createIdempotencyKey = null;
      if (res.duplicate) {
        this.campaignStatus = res.status_payload as CampaignStatus;
        this.startPolling(res.campaign_id);
        return;
      }
      this.step = 'confirm';
    }, (err) => {
      this.loading = false;
      if (err?.status === 409) {
        this.error = 'This campaign was already submitted with different details';
      } else {
        this.error = this._errorMessage(err);
      }
    });
  }

  sendCampaign() {
    if (!this.createResponse) {
      return;
    }
    this.loading = true;
    this.error = null;
    this.step = 'sending';
    this._sendIdempotencyKey = this._sendIdempotencyKey || (crypto as any).randomUUID();
    this._campaignService.sendCampaign(this.createResponse.campaign_id, this._sendIdempotencyKey).subscribe((res) => {
      this.loading = false;
      this._sendIdempotencyKey = null;
      this.campaignStatus = res.status_payload;
      this.startPolling(this.createResponse.campaign_id);
    }, (err) => {
      this.loading = false;
      this.step = 'confirm';
      this.error = this._errorMessage(err);
    });
  }

  startPolling(campaignId: string) {
    this.step = 'status';
    this._statusSubscription?.unsubscribe();
    let intervalMs = this.campaignStatus?.recommended_poll_interval_ms || 1500;
    this._statusSubscription = this._campaignService.pollStatus(campaignId, intervalMs).subscribe((res) => {
      this.campaignStatus = res.status;
      this.pollError = res.pollError;
      this.pollFailed = res.pollFailed;
    }, (err) => {
      this.error = this._errorMessage(err);
    });
  }

  get zeroRecipients(): boolean {
    return !!this.previewResponse && this.previewResponse.deduplicated_recipient_count === 0;
  }

  close() {
    this.dialogRef.close(!!this.campaignStatus);
  }

  private _errorMessage(err: any): string {
    return err?.error?.error || err?.error?.detail || err?.error?.details || 'An error occurred. Please check your connection and try again.';
  }
}
